/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { History } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { SECTION_LABELS } from './soapConfig';

/** Anteprima a 4 riquadri (S/O/A/P) del blocco SOAP corrente. */
export const SoapPreviewGrid = ({ soap }) => (
  <div className="grid grid-cols-2 gap-1.5" data-testid="soap-preview">
    {Object.entries(SECTION_LABELS).map(([key, meta]) => {
      const val = soap[key];
      return (
        <div
          key={key}
          className={`rounded-md px-2 py-1.5 text-[11px] leading-snug ${val ? meta.color : 'bg-muted/40 text-muted-foreground'}`}
          data-testid={`soap-section-${key}`}
        >
          <div className="font-bold text-[10px] uppercase tracking-wider mb-0.5">{meta.label}</div>
          <div className="line-clamp-2 min-h-[1.5em]">{val || <span className="opacity-50">—</span>}</div>
        </div>
      );
    })}
  </div>
);

const HistoryEntry = ({ h }) => {
  const meta = SECTION_LABELS[h.section] || { label: h.section, color: 'bg-muted text-muted-foreground' };
  const time = new Date(h.at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return (
    <li className={`rounded-md border border-border/50 p-2 flex items-start gap-2 ${meta.color}`} data-testid={`soap-history-entry-${h.section}`}>
      <div className="text-[10px] font-mono opacity-70 flex-shrink-0 pt-0.5" title={h.at}>{time}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[10px] font-bold uppercase tracking-wider">{meta.label}</span>
          {h.lang && (
            <Badge variant="outline" className="text-[9px] font-mono px-1 py-0 h-4">{h.lang.toUpperCase()}</Badge>
          )}
        </div>
        <div className="text-[11px] leading-snug break-words">{h.text}</div>
      </div>
    </li>
  );
};

/** Cronologia collassabile dei blocchi dettati. */
export const SoapHistory = ({ history, open, onOpenChange }) => {
  if (!history.length) return null;
  return (
    <Collapsible open={open} onOpenChange={onOpenChange} data-testid="soap-history">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:text-foreground rounded-md py-1 px-2 bg-background/40"
          data-testid="soap-history-toggle"
        >
          <span className="flex items-center gap-1.5">
            <History className="h-3.5 w-3.5" />
            Cronologia dettatura ({history.length} bloc{history.length === 1 ? 'co' : 'chi'})
          </span>
          <span className="text-[10px]">{open ? '▲' : '▼'}</span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <ol className="space-y-1.5 max-h-52 overflow-y-auto scrollbar-thin pr-1" data-testid="soap-history-list">
          {history.map((h) => <HistoryEntry key={h.id} h={h} />)}
        </ol>
      </CollapsibleContent>
    </Collapsible>
  );
};
